/**
 * Client API for the YouTube Downloader feature. Thin wrappers over the
 * Portal /api/youtube routes. Status polling is intentionally caller-
 * controlled (set/clearInterval in the page) so the page can stop
 * polling on unmount or job-terminal transitions.
 */

export type Quality = 'best' | '1080p' | '720p' | '480p' | 'audio'
export type Format = 'mp4' | 'mp3'
export type Status =
  | 'pending'
  | 'downloading'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'canceled'

export interface YoutubeJob {
  jobId: string
  youtubeUrl: string
  videoId: string
  videoTitle: string
  channelName: string
  durationSec: number
  thumbnailUrl: string
  quality: Quality
  format: Format
  status: Status
  progress: number
  errorMessage: string
  fileSizeBytes: number
  completedAt: string | null
  expiresAt: string | null
  createdAt: string
}

export interface YoutubeStatus {
  termsAcceptedAt: string | null
  activeCount: number
  activeLimit: number
}

interface ApiError {
  error: string
}

const FETCH_TIMEOUT_MS = 15000

async function readErr(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ApiError
    return body.error ?? `http_${res.status}`
  } catch {
    return `http_${res.status}`
  }
}

/**
 * fetch wrapper with a 15s AbortController so a hung request (e.g. when
 * the Portal server can't reach Mongo) doesn't leave the UI stuck in a
 * "Saving..." spinner. The thrown error is normalized to `timeout`.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error('timeout')
    }
    throw err
  } finally {
    window.clearTimeout(timer)
  }
}

export async function getYoutubeStatus(): Promise<YoutubeStatus> {
  const res = await fetchWithTimeout('/api/youtube/status', { credentials: 'same-origin' })
  if (!res.ok) throw new Error(await readErr(res))
  return (await res.json()) as YoutubeStatus
}

export async function acceptYoutubeTerms(): Promise<void> {
  const res = await fetchWithTimeout('/api/youtube/accept-terms', {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) throw new Error(await readErr(res))
}

export interface CreateDownloadInput {
  youtubeUrl: string
  quality: Quality
  format: Format
}

export async function createDownload(
  input: CreateDownloadInput,
): Promise<{ jobId: string }> {
  const res = await fetchWithTimeout('/api/youtube/download', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(await readErr(res))
  return (await res.json()) as { jobId: string }
}

export async function getJob(jobId: string): Promise<YoutubeJob> {
  const res = await fetchWithTimeout(`/api/youtube/download/${jobId}/status`, {
    credentials: 'same-origin',
  })
  if (!res.ok) throw new Error(await readErr(res))
  return (await res.json()) as YoutubeJob
}

export async function cancelJob(jobId: string): Promise<void> {
  const res = await fetchWithTimeout(`/api/youtube/cancel/${jobId}`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) throw new Error(await readErr(res))
}

export async function getHistory(): Promise<YoutubeJob[]> {
  const res = await fetchWithTimeout('/api/youtube/history', { credentials: 'same-origin' })
  if (!res.ok) throw new Error(await readErr(res))
  const body = (await res.json()) as { downloads: YoutubeJob[] }
  return body.downloads
}

export function isTerminal(status: Status): boolean {
  return (
    status === 'completed' ||
    status === 'failed' ||
    status === 'expired' ||
    status === 'canceled'
  )
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function formatDuration(sec: number): string {
  if (sec <= 0) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  if (m < 60) return `${m}:${s.toString().padStart(2, '0')}`
  const h = Math.floor(m / 60)
  return `${h}:${(m % 60).toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}
